import { useEffect, useRef, type ReactNode } from "react";
import { Animated, StyleSheet, View } from "react-native";

import { useReducedMotion } from "./useReducedMotion";
import type { UiTheme } from "./theme";

// THE "this control is here, and you have not found it yet" cue.
//
// Wraps an icon-only affordance and announces it until it has been used once,
// then goes quiet forever. It teaches a glyph without spending the row's width
// on words — which is what makes it the answer where a label does not fit
// ([[feedback_instructional_overlay_pulses]]: it appears, it is read, it goes
// away).
//
// ⭐ It owns BOTH announcements, and that is the whole point of it being a
// component rather than the hook it started as (EQ-1-GYM-COLDSTART R7 → R8,
// Antenore 2026-08-17):
//
//   motion allowed  → the glyph BREATHES. That is the entire signal.
//   reduced motion  → a static MARK instead. ⛔ Not silence: respecting the
//                     setting must not cost the athlete the only way to notice
//                     the control exists. His decision, and it rules out both
//                     extremes — animating anyway, and going quiet.
//   seen            → nothing, in either setting.
//
// ⛔ The two never co-occur. A permanent dot beside a breathing glyph is two
// announcements for one thing, so the mark is gated on reduced motion, not on
// "unseen" alone.
//
// ⛔ A COMPONENT, not a hook returning a style + a dot to render. Two callsites
// (the coach-notes "?" and the ✕ that declares gear missing) would otherwise
// each place their own mark and each re-decide the branch — and this change
// exists precisely to collapse a duplicate, so producing a new one would be
// self-defeating (§CONSOLIDAMENTO). One place, both branches, no callsite
// deciding anything.
//
// ⛔ NEITHER announcement may touch layout or the tap target. The breath scales
// the GLYPH, never the button box; the mark is absolutely positioned and
// `pointerEvents="none"`. The ✕ sits 2pt from the gear chip's touch zone —
// measured, and already tight — so a cue that grew the box would put two tap
// zones on the same pixels.

/** How far the glyph grows at the top of the breath. */
const PEAK = 1.18;
/** Half-period, ms. Slow enough to read as breathing, not as a warning. */
const HALF_PERIOD_MS = 700;
/**
 * Mark geometry, exported because it is CHECKED rather than asserted.
 *
 * ⚠️ The first version had `size: 6, offset: 2` and a comment claiming the mark
 * sat "outside the glyph's box so it does not sit on the strokes it is
 * marking". That was wrong, and wrong in the way a comment cannot catch: 2pt
 * clears the icon's square BOUNDING BOX, but not its INK. Lucide draws on a
 * 24-grid where `help` is `circle r=10` with a 2-unit stroke, so its ink
 * reaches 11/24 of the rendered size from centre — straight through where the
 * mark was. Caught by an a11y review computing the path geometry, 2026-08-17.
 *
 * ⇒ the numbers below are pinned by `unseenCue.test.tsx`, which recomputes the
 * clearance for both callsite sizes from THIS object. If someone retunes them,
 * the test does the geometry again instead of trusting the prose.
 * [[feedback_headless_visual_constraints]] — what cannot be rendered here gets
 * constrained mechanically instead.
 */
export const UNSEEN_MARK_GEOMETRY = {
  /** Diameter. 8 is the standard unread-badge size; 6 was too timid for a
   *  mark that carries the whole announcement on its own. */
  size: 8,
  /** How far past the glyph's box the mark is pushed, on both axes. */
  offset: 6,
  /**
   * How far an icon's INK reaches from its centre, as a fraction of the
   * rendered size. Lucide's 24-grid: content radius 10, plus half of the
   * 2-unit stroke. Every glyph in the set fits inside it, so a clearance
   * computed against this holds for a future icon too, not just these two.
   */
  inkRadiusRatio: 11 / 24,
} as const;

const DOT = UNSEEN_MARK_GEOMETRY.size;
const OFFSET = UNSEEN_MARK_GEOMETRY.offset;

export type UnseenCueProps = {
  theme: UiTheme;
  /** True once the athlete has used the control — silences both branches. */
  seen: boolean;
  /** The glyph being announced. */
  children: ReactNode;
};

export function UnseenCue({ theme, seen, children }: UnseenCueProps) {
  const scale = useRef(new Animated.Value(1)).current;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (seen || reducedMotion) {
      // Back to rest explicitly: the value outlives the effect that moved it,
      // so a control that becomes seen mid-breath would otherwise freeze at
      // whatever scale it had reached.
      scale.setValue(1);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(scale, {
          toValue: PEAK,
          duration: HALF_PERIOD_MS,
          // A transform, so it runs off the JS thread and keeps breathing
          // while the logger re-renders on every logged set.
          useNativeDriver: true,
        }),
        Animated.timing(scale, {
          toValue: 1,
          duration: HALF_PERIOD_MS,
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [seen, reducedMotion, scale]);

  const showMark = !seen && reducedMotion;

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      {children}
      {showMark ? (
        <View
          style={[styles.mark, { backgroundColor: theme.palette.brandText }]}
          // ⛔ Decorative, and deliberately mute. The control it marks already
          // announces itself with a name AND a hint; a second node here would
          // make one affordance speak twice, and the thing the mark stands in
          // for — motion — was never announced to a screen reader anyway.
          pointerEvents="none"
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        />
      ) : null}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  // ⚠️ `brandText`, and the two colours it is NOT are both instructive
  // (measured in unseenCueContrast.test.ts, across every family and both
  // modes):
  //
  //   brand500  — the colour the unread "?" glyph itself turns. Bottoms out at
  //               2.37:1 on rosepine/light surface2, under even the 3:1
  //               non-text floor: invisible exactly where this mark is the
  //               ONLY signal left. The obvious choice, and wrong.
  //   brand600  — clears the floor at 3.41:1, but `design-tokens.ts` says it
  //               is being retired ("stays put until VB-PAL-2/VB-PAL-3 empty
  //               its call sites"), and a new callsite pushes that migration
  //               backwards. It is also the PRESSED-FILL half of the old dual
  //               role, which this is not.
  //   brandText — the brand hue as a FOREGROUND mark, which is what a dot on a
  //               surface is. 4.52:1 at its worst.
  //
  // ⭐ Held to the 4.5 TEXT floor although it is not text. A 6pt dot is small,
  // and under reduced motion it carries the whole announcement on its own, so
  // the 3:1 non-text minimum is the wrong bar to scrape — the reader this is
  // for is myopic. It also keeps the mark in the brand family, so it reads as
  // "new" rather than as "disabled" (which a neutral grey would).
  //
  // Offset far enough that the mark clears the glyph's INK, not merely its
  // bounding box — see UNSEEN_MARK_GEOMETRY, and the test that recomputes it.
  // Still inside the 36pt button at both callsites, so nothing clips.
  mark: {
    position: "absolute",
    top: -OFFSET,
    right: -OFFSET,
    width: DOT,
    height: DOT,
    borderRadius: DOT / 2,
  },
});
